'use client'

import { useEffect, useRef, useState } from 'react'
import type { Team } from '@/payload-types'
import { Media } from '@/components/Media'
import { cn } from '@/utilities/ui'

export interface TeamMemberCardProps {
  teamMember: Team
  index?: number
  active?: boolean
}

export default function TeamMemberCard({ teamMember, index = 0, active }: TeamMemberCardProps) {
  const fullName = `${teamMember.prename} ${teamMember.lastname}`.trim()
  const description = teamMember.description || ''

  const [isInView, setIsInView] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = cardRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const isReady = isInView

  const isPriority = index < 3

  return (
    <div
      ref={cardRef}
      className={cn(
        'flex flex-col gap-2 items-center max-w-[350px] min-w-[300px]',
        'opacity-0 translate-y-12 transition-[opacity,transform] duration-700 ease-out',
        isReady && 'opacity-100 translate-y-0',
      )}
    >
      <div className="relative w-full h-80 overflow-hidden group">
        {active === undefined ? (
          <div
            className={cn(
              'absolute bottom-0 left-0 right-0 bg-brand transform -rotate-10 scale-150 translate-y-10 origin-center z-0 transition-[top] duration-700 ease-out delay-200',
              isReady ? 'top-[33.3333%]' : 'top-full',
            )}
          />
        ) : (
          <div
            className={cn(
              'absolute bottom-0 left-0 right-0 bg-brand transform -rotate-10 scale-150 translate-y-10 origin-center z-0 transition-[top] duration-700 ease-out',
              active ? 'top-[33.3333%]' : 'top-full',
            )}
          />
        )}

        {teamMember.image && (
          <Media
            resource={teamMember.image}
            fill
            pictureClassName="absolute inset-0"
            imgClassName="object-contain object-center relative z-10"
            size="(max-width: 640px) 300px, (max-width: 1024px) 350px, 350px"
            priority={isPriority}
            fetchPriority={isPriority ? 'high' : 'auto'}
            loading={isPriority ? undefined : 'lazy'}
            disableBlur={true}
            alt={
              typeof teamMember.image === 'object' && teamMember.image.alt
                ? teamMember.image.alt
                : fullName
            }
          />
        )}
      </div>

      <div className="text-center">
        <h3 className="title-sm text-primary uppercase">{fullName}</h3>
        <p className="body-base text-text-secondary">{description}</p>
        {teamMember.email && (
          <a
            href={`mailto:${teamMember.email}`}
            className="body-base text-brand hover:text-primary transition-colors"
          >
            {teamMember.email}
          </a>
        )}
      </div>
    </div>
  )
}
